import { useEffect, useState } from 'react';
import { isNil, isNotNil, isWeb } from 'shared/src/utils';

/**
 * url 의 HTML 을 직접 fetch 해서 `<title>` 태그를 추출한다. (네이티브 전용)
 *
 * 네이티브 WebView(RNAblyWebView)는 document.title 변경 이벤트를 항상 전달하지
 * 않으므로, 페이지 레벨에서 서버가 내려준 `<title>` 을 읽어 헤더 타이틀로 쓴다.
 *
 * 웹은 브라우저 CORS 정책상 다른 origin 의 문서를 fetch 할 수 없고, 대신 iframe
 * same-origin 이라 WebView 의 `onTitleChange`(MutationObserver)로 title 을 얻을 수
 * 있으므로 이 훅은 웹에서 no-op 이다.
 *
 * @param url title 을 추출할 페이지 URL
 * @param enabled false 면 fetch 하지 않음(caller 가 이미 title 을 알거나 헤더가
 * 숨겨져 title 이 필요없는 경우).
 */
export const useHtmlDocumentTitle = (
  url: string | undefined,
  { enabled }: { enabled: boolean },
): string | undefined => {
  const [title, setTitle] = useState<string | undefined>(undefined);

  useEffect(() => {
    // 웹은 CORS 로 cross-origin fetch 가 막힌다 → onTitleChange 경로로 위임.
    if (isWeb || !enabled || isNil(url)) return;

    let cancelled = false;
    fetch(url, { credentials: 'include' })
      .then(response => response.text())
      .then(html => {
        if (cancelled) return;
        const extracted = extractTitle(html);
        if (isNotNil(extracted)) setTitle(extracted);
      })
      .catch(() => {
        // title 을 못 읽어도 무시 — 기존(caller 가 넘긴) 값을 그대로 사용
      });

    return () => {
      cancelled = true;
    };
  }, [url, enabled]);

  return title;
};

const extractTitle = (html: string): string | undefined => {
  const match = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  if (!isNotNil(match)) return undefined;
  const decoded = decodeHtmlEntities(match[1].trim());
  return decoded.length > 0 ? decoded : undefined;
};

const decodeHtmlEntities = (value: string): string =>
  value
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&#x0*27;/gi, "'")
    .replace(/&nbsp;/g, ' ');
